import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/auth/use-user';

interface UseInfiniteUserPagesOptions {
  searchQuery?: string;
  limit?: number;
  excludeProjectId?: string; // Exclude pages already assigned to this project
}

interface PageData {
  data: {
    id: string;
    title: string;
    type: string | null;
    content: string;
    created_at: string;
    updated_at: string;
    workbook_id: string | null;
    user_id: string;
    writing_style: string | null;
    initial_request: string | null;
    openai_thread_id: string | null;
  }[];
  nextPage?: number;
  hasMore: boolean;
}

/**
 * Hook for infinite scrolling user pages with search
 * Shows pages that are NOT assigned to the current project
 * Includes: pages with workbook_id = null (private) OR workbook_id = other_project_id
 * Excludes: pages with workbook_id = current_project_id
 */
export const useInfiniteUserPages = (options: UseInfiniteUserPagesOptions = {}) => {
  const { user } = useUser();
  const { searchQuery = '', limit = 50, excludeProjectId } = options;

  return useInfiniteQuery<PageData, Error, { pages: PageData[]; pageParams: number[] }, (string | number)[], number>({
    queryKey: ['infinite-user-pages', user?.id, searchQuery, limit, excludeProjectId],
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }): Promise<PageData> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const offset = pageParam * limit;

      // Build query with search filter if provided
      let query = supabase
        .from('workbook_page_content')
        .select(`
          id,
          title,
          type,
          content,
          created_at,
          updated_at,
          workbook_id,
          user_id,
          writing_style,
          initial_request,
          openai_thread_id
        `, { count: 'exact' })
        .eq('user_id', user.id);

      // Add search filter if provided
      if (searchQuery.trim()) {
        query = query.or(`title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%,type.ilike.%${searchQuery}%`);
      }

      // Exclude pages already assigned to the current project
      // This should show: pages with workbook_id = null OR workbook_id = other_project_ids
      // But exclude: pages with workbook_id = current_project_id
      if (excludeProjectId) {
        query = query.or(`workbook_id.is.null,workbook_id.neq.${excludeProjectId}`);
      }

      // Add pagination and ordering
      const { data: pages, error, count } = await query
        .order('updated_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error fetching user pages:', error);
        throw error;
      }

      const hasMore = (pages?.length || 0) === limit;
      const nextPage = hasMore ? pageParam + 1 : undefined;

      // Get unique workbook IDs to fetch project titles
      const workbookIds = [...new Set(pages?.filter(page => page.workbook_id).map(page => page.workbook_id))];
      
      let projects: Record<string, { id: string; title: string }> = {};
      if (workbookIds.length > 0) {
        const { data: workbookData, error: workbookError } = await supabase
          .from('workbook')
          .select('id, title')
          .in('id', workbookIds);
          
        if (!workbookError && workbookData) {
          projects = workbookData.reduce((acc, wb) => {
            acc[wb.id] = { id: wb.id, title: wb.title };
            return acc;
          }, {} as Record<string, { id: string; title: string }>);
        }
      }

      // Transform the data to include project information
      const transformedData = (pages || []).map(item => ({
        ...item,
        project: item.workbook_id && projects[item.workbook_id] ? projects[item.workbook_id] : null
      }));

      return {
        data: transformedData,
        nextPage,
        hasMore,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}; 